import type { RefObject } from 'react';

import { checkIfValueTypeAlignment } from './helpers.js';
import { buildTimeframeClipboardText } from './selection-copy-helpers.js';
import type { DateTimeInputRef } from './types.js';
import { getStratoLogger } from '../../core/sdk/sdk-logger.js';
import type { SpinButtonHandler } from '../spin-buttons/spin-button-group/types.js';

function warnSelectionFailed(message: string, error: unknown): void {
  getStratoLogger('DateTimeRangePicker').warn(message, error);
}

export function safeSetBaseAndExtent(
  selection: Selection | null | undefined,
  { anchorNode, anchorOffset, focusNode, focusOffset }: SelectionEndpoints,
  warnMessage: string,
): void {
  try {
    selection?.setBaseAndExtent(
      anchorNode,
      anchorOffset,
      focusNode,
      focusOffset,
    );
  } catch (error) {
    warnSelectionFailed(warnMessage, error);
  }
}

/**
 * Extracts selection endpoints for multi-element selections.
 * Also handles the Safari case where a container element is reported as both anchor and focus
 * but spans multiple children (same ELEMENT_NODE, different offsets).
 * Returns null for collapsed, single-node text selections, or empty selections.
 */
function getMultiElementEndpoints(
  selection: Selection | null,
): SelectionEndpoints | null {
  if (
    !selection?.anchorNode ||
    !selection.focusNode ||
    selection.toString().length === 0
  ) {
    return null;
  }
  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  const isSameNode = anchorNode === focusNode;
  if (
    isSameNode &&
    !(anchorNode.nodeType === Node.ELEMENT_NODE && anchorOffset !== focusOffset)
  ) {
    return null;
  }
  return { anchorNode, anchorOffset, focusNode, focusOffset };
}

/** Helper to walk backwards through text nodes. */
function previousTextNode(root: HTMLElement, node: Node): Node | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);

  let current = walker.lastChild();
  let found = false;
  while (current) {
    if (found) {
      // Skip hidden text nodes by checking parent value types.
      if (checkIfValueTypeAlignment(current)) {
        return current;
      }
    }

    if (current === node) {
      found = true;
    }

    current = walker.previousNode();
  }

  return null;
}

/** Helper to walk forwards through text nodes. */
function nextTextNode(root: HTMLElement, node: Node): Node | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);

  let current = walker.nextNode();
  let found = false;

  while (current) {
    if (found) {
      // Skip hidden text nodes by checking parent value types.
      if (checkIfValueTypeAlignment(current)) {
        return current;
      }
    }
    if (current === node) {
      found = true;
    }
    current = walker.nextNode();
  }

  return null;
}

/** Helper that finds the next character position to adjust the global selection. */
function findNextCharacterPosition(
  root: HTMLElement,
  node: Node,
  offset: number,
): { node: Node; offset: number } | null {
  if (
    node.nodeType === Node.TEXT_NODE &&
    node.nodeValue &&
    offset < node.nodeValue.length &&
    checkIfValueTypeAlignment(node)
  ) {
    return { node, offset: offset + 1 };
  }

  // Element node: offset is a child index — walk into that child to find the first text node.
  if (node.nodeType !== Node.TEXT_NODE) {
    const childAtOffset = node.childNodes[offset];
    if (childAtOffset) {
      const walker = document.createTreeWalker(
        childAtOffset,
        NodeFilter.SHOW_TEXT,
        null,
      );
      const firstText =
        walker.firstChild() ??
        (childAtOffset.nodeType === Node.TEXT_NODE ? childAtOffset : null);
      if (
        firstText &&
        (checkIfValueTypeAlignment(firstText) ||
          getClosestContentEditable(firstText) !== null)
      ) {
        return { node: firstText, offset: 1 };
      }
    }

    // At element's end boundary: find the last text node inside and step to the next.
    if (offset > 0) {
      const previousChild = node.childNodes[offset - 1];
      if (previousChild) {
        const walker = document.createTreeWalker(
          previousChild,
          NodeFilter.SHOW_TEXT,
          null,
        );
        const lastText =
          walker.lastChild() ??
          (previousChild.nodeType === Node.TEXT_NODE ? previousChild : null);
        if (lastText) {
          let nextFromBoundary: Node | null = nextTextNode(root, lastText);
          while (
            nextFromBoundary !== null &&
            getClosestContentEditable(nextFromBoundary) === null
          ) {
            nextFromBoundary = nextTextNode(root, nextFromBoundary);
          }
          if (nextFromBoundary) {
            return { node: nextFromBoundary, offset: 1 };
          }
        }
      }
    }
  }

  // Keep separator text nodes as valid step targets for Shift+Arrow.
  let next: Node | null = nextTextNode(root, node);

  // Skip separator nodes between two expression textboxes for one-step crossing.
  if (
    isExpressionNode(node) &&
    next &&
    getClosestContentEditable(next) === null
  ) {
    const nextEditableNode: Node | null = nextTextNode(root, next);
    if (isExpressionNode(nextEditableNode)) {
      next = nextEditableNode;
    }
  }

  if (!next) {
    return null;
  }

  return { node: next, offset: 1 };
}

/** Helper that finds the previous character position to adjust the global selection. */
function findPreviousCharacterPosition(
  root: HTMLElement,
  node: Node,
  offset: number,
): { node: Node; offset: number } | null {
  if (
    node.nodeType === Node.TEXT_NODE &&
    offset > 0 &&
    checkIfValueTypeAlignment(node)
  ) {
    return { node, offset: offset - 1 };
  }

  // Element node: offset is a child index — walk into offset-1 child to find the last text node (mirrors forward logic).
  if (node.nodeType !== Node.TEXT_NODE && offset > 0) {
    const childAtOffset = node.childNodes[offset - 1];
    if (childAtOffset) {
      const walker = document.createTreeWalker(
        childAtOffset,
        NodeFilter.SHOW_TEXT,
        null,
      );
      const lastText =
        walker.lastChild() ??
        (childAtOffset.nodeType === Node.TEXT_NODE ? childAtOffset : null);
      if (
        lastText?.nodeValue &&
        (checkIfValueTypeAlignment(lastText) ||
          getClosestContentEditable(lastText) !== null)
      ) {
        return { node: lastText, offset: lastText.nodeValue.length - 1 };
      }
    }
  }

  // Keep separator text nodes as valid backward step targets for Shift+Arrow.
  let prev: Node | null = previousTextNode(root, node);
  if (
    isExpressionNode(node) &&
    prev &&
    getClosestContentEditable(prev) === null
  ) {
    const previousEditableNode: Node | null = previousTextNode(root, prev);
    if (isExpressionNode(previousEditableNode)) {
      prev = previousEditableNode;
    }
  }
  if (!prev || prev?.nodeValue === null) {
    return null;
  }

  return { node: prev, offset: prev.nodeValue.length - 1 };
}

/**
 * Empty spin buttons render their placeholder via CSS `:empty::before`, so they
 * have no text-node child. We still want them inside selections produced by
 * Ctrl/Cmd+A so the copy handler can substitute the placeholder text.
 */
function isEmptyPlaceholderSpinButton(node: Node): boolean {
  return (
    node instanceof Element &&
    node.getAttribute('role') === 'spinbutton' &&
    node.childNodes.length === 0
  );
}

/** Visits text nodes plus empty placeholder spin-button elements in document order. */
function createSelectionPositionWalker(root: HTMLElement): TreeWalker {
  return document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    {
      acceptNode: (node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return NodeFilter.FILTER_ACCEPT;
        }
        return isEmptyPlaceholderSpinButton(node)
          ? NodeFilter.FILTER_ACCEPT
          : NodeFilter.FILTER_SKIP;
      },
    },
  );
}

/** Helper to find the first selection-anchor position to apply selection. */
function findFirstCharacterPosition(
  root: HTMLElement,
): { node: Node; offset: number } | null {
  const walker = createSelectionPositionWalker(root);
  let current = walker.firstChild();
  while (current !== null) {
    // Skip hidden nodes (e.g. relative date expression values when in absolute mode).
    if (checkIfValueTypeAlignment(current)) {
      return { node: current, offset: 0 };
    }
    current = walker.nextNode();
  }
  return null;
}

/** Helper to find the last selection-anchor position to apply selection. */
function findLastCharacterPosition(
  root: HTMLElement,
): { node: Node; offset: number } | null {
  const walker = createSelectionPositionWalker(root);
  let current = walker.lastChild();
  while (current !== null) {
    // Skip hidden nodes (e.g. relative date expression values when in absolute mode).
    if (checkIfValueTypeAlignment(current)) {
      const offset =
        current.nodeType === Node.TEXT_NODE
          ? current.nodeValue?.length ?? 0
          : 0;
      return { node: current, offset };
    }
    current = walker.previousNode();
  }
  return null;
}

function compareSelectionPoints(
  firstNode: Node,
  firstOffset: number,
  secondNode: Node,
  secondOffset: number,
): number {
  if (firstNode === secondNode) {
    return firstOffset - secondOffset;
  }

  // Guard: compareBoundaryPoints throws WrongDocumentError when nodes belong
  // to different document trees (e.g. a stale detached text node that the
  // browser's Selection still references after a DOM mutation).
  if (firstNode.getRootNode() !== secondNode.getRootNode()) {
    return 0;
  }

  const firstRange = document.createRange();
  firstRange.setStart(firstNode, firstOffset);
  firstRange.collapse(true);

  const secondRange = document.createRange();
  secondRange.setStart(secondNode, secondOffset);
  secondRange.collapse(true);

  try {
    return firstRange.compareBoundaryPoints(Range.START_TO_START, secondRange);
  } catch {
    // compareBoundaryPoints throws a WrongDocumentError when one of the nodes
    // is detached from the document (its root is itself, not `document`).
    // Fall back to compareDocumentPosition which works with detached nodes.
    const pos = firstNode.compareDocumentPosition(secondNode);
    if (pos & Node.DOCUMENT_POSITION_FOLLOWING) {
      return -1;
    }
    if (pos & Node.DOCUMENT_POSITION_PRECEDING) {
      return 1;
    }
    return 0;
  }
}

function buildDocumentOrderedRange({
  anchorNode,
  anchorOffset,
  focusNode,
  focusOffset,
}: SelectionEndpoints): Range {
  const range = document.createRange();
  const anchorBeforeFocus =
    compareSelectionPoints(anchorNode, anchorOffset, focusNode, focusOffset) <=
    0;
  if (anchorBeforeFocus) {
    range.setStart(anchorNode, anchorOffset);
    range.setEnd(focusNode, focusOffset);
  } else {
    range.setStart(focusNode, focusOffset);
    range.setEnd(anchorNode, anchorOffset);
  }
  return range;
}

function selectionOverlapsSpinButton(
  selectionStartNode: Node,
  selectionStartOffset: number,
  selectionEndNode: Node,
  selectionEndOffset: number,
  spinButton: HTMLElement,
): boolean {
  const spinButtonStart = findFirstCharacterPosition(spinButton);
  const spinButtonEnd = findLastCharacterPosition(spinButton);

  if (!spinButtonStart || !spinButtonEnd) {
    return false;
  }

  return (
    compareSelectionPoints(
      selectionStartNode,
      selectionStartOffset,
      spinButtonEnd.node,
      spinButtonEnd.offset,
    ) < 0 &&
    compareSelectionPoints(
      selectionEndNode,
      selectionEndOffset,
      spinButtonStart.node,
      spinButtonStart.offset,
    ) > 0
  );
}

/**
 * Restores [contenteditable] and tabindex to all elements inside `root` that
 * had those attributes temporarily stripped (marked with [data-wascontenteditable]
 * / [data-wastabindex]).
 */
function restoreContentEditableAttributes(root: HTMLElement): void {
  for (const editable of root.querySelectorAll<HTMLElement>(
    '[data-wascontenteditable]',
  )) {
    const previousValue = editable.getAttribute('data-wascontenteditable');
    if (previousValue !== null) {
      editable.setAttribute('contenteditable', previousValue);
    }
    editable.removeAttribute('data-wascontenteditable');

    const wasTabIndex = editable.getAttribute('data-wastabindex');
    if (wasTabIndex !== null) {
      editable.setAttribute('tabindex', wasTabIndex);
      editable.removeAttribute('data-wastabindex');
    }
  }
  // Clear drag-selected marks when CE is restored — :focus or native selection takes over.
  for (const el of root.querySelectorAll<HTMLElement>('[data-drag-selected]')) {
    el.removeAttribute('data-drag-selected');
  }
}

/**
 * Marks elements intersected by `range` with [data-drag-selected] for element-level accent styling.
 * Textboxes are excluded during live drags (native ::selection shows char-level highlight) and
 * included only for snapped post-mouseup ranges when `includeTextboxes` is true.
 * Element-level styling is used because Safari doesn't resolve CSS vars inside ::selection
 * on non-contenteditable elements.
 */
function markDragSelectedElements(
  rootElement: HTMLElement,
  range: Range,
  { includeTextboxes = false }: { includeTextboxes?: boolean } = {},
): void {
  for (const el of rootElement.querySelectorAll<HTMLElement>(
    '[data-drag-selected]',
  )) {
    el.removeAttribute('data-drag-selected');
  }

  // (a) Direct children of SpinButtonGroups: spinbuttons + separators
  for (const group of rootElement.querySelectorAll(
    '[data-valuetype="iso8601"]',
  )) {
    for (const child of group.children) {
      if (range.intersectsNode(child)) {
        child.setAttribute('data-drag-selected', '');
      }
    }
  }

  // (b) Expression textbox inputs — only for snapped post-mouseup ranges
  if (includeTextboxes) {
    for (const textbox of rootElement.querySelectorAll('[role="textbox"]')) {
      if (range.intersectsNode(textbox)) {
        textbox.setAttribute('data-drag-selected', '');
      }
    }
  }
}

// Tracks pending CE-restore AbortControllers per picker root.
// CE is stripped during multi-element drags (Safari clips cross-element addRange to the active CE host)
// and restored on the next real interaction. WeakMap avoids retaining roots after unmount.
const pendingRestoreSignals = new WeakMap<HTMLElement, AbortController>();

/**
 * Endpoints captured by `deferContentEditableRestore` before re-attaching CE.
 * Safari clips the live selection to the active CE host after CE re-attachment,
 * so a bubble-phase Backspace/Delete handler can no longer see the full
 * multi-element range. Consumers read these to feed `planBulkClear`.
 * @internal
 */
export type SelectionEndpoints = {
  anchorNode: Node;
  anchorOffset: number;
  focusNode: Node;
  focusOffset: number;
};
const lastPreRestoreEndpoints = new WeakMap<HTMLElement, SelectionEndpoints>();

/**
 * Returns and clears the multi-element selection endpoints captured for `root`
 * by `deferContentEditableRestore` just before the most recent CE re-attach.
 * Returns `undefined` when no stash exists (no preceding multi-element
 * selection, or already consumed).
 * @internal
 */
export function consumePreRestoreEndpoints(
  root: HTMLElement,
): SelectionEndpoints | undefined {
  const stash = lastPreRestoreEndpoints.get(root);
  lastPreRestoreEndpoints.delete(root);
  return stash;
}

// Returns true when the keyboard event should keep CE stripped (selection extension / copy / cut).
function shouldBypassRestore(e: KeyboardEvent): boolean {
  if (['Meta', 'Control', 'Alt', 'Shift'].includes(e.key)) {
    return true;
  }
  if (
    (e.metaKey || e.ctrlKey) &&
    (e.key.toLowerCase() === 'c' || e.key.toLowerCase() === 'x')
  ) {
    return true;
  }
  // Shift+Arrow/Home/End extends the current selection — keep CE stripped
  // so each subsequent step can cross spinbutton boundaries in Safari.
  return (
    e.shiftKey &&
    ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(
      e.key,
    )
  );
}

// Plain ArrowLeft/Right with a live multi-element selection: collapse to the directional
// boundary before CE is restored. Firefox's default would shrink the selection one step at
// a time (mirror of Shift+Arrow extension), forcing the user to press Arrow N times to
// "use up" a previous Shift+Arrow selection of N steps before the cursor moves.
// Chrome/Safari already collapse natively; this normalises Firefox.
// Done here (capture phase) because Firefox discards cross-element selections as soon as
// [contenteditable] is added back. Returns true when the event was consumed.
function tryCollapseArrow(e: KeyboardEvent, root: HTMLElement): boolean {
  if (
    e.shiftKey ||
    e.metaKey ||
    e.ctrlKey ||
    e.altKey ||
    (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
  ) {
    return false;
  }
  const collapsed = collapseMultiElementSelection(
    root,
    e.key === 'ArrowLeft' ? 'left' : 'right',
  );
  if (collapsed) {
    e.preventDefault();
    // Stop the event so the boundary spinbutton's own keydown handler
    // doesn't immediately step further (handleArrowLeft would see
    // selection.start === 0 after onFocus → selectAll and move past it).
    e.stopPropagation();
  }
  return collapsed;
}

/**
 * Reads but does not consume the pre-restore endpoints for `root`.
 * Use when you need to inspect the stash without preventing a later consumer
 * (e.g. planBulkClear via consumePreRestoreEndpoints) from reading it.
 * @internal
 */
export function peekPreRestoreEndpoints(
  root: HTMLElement,
): SelectionEndpoints | undefined {
  return lastPreRestoreEndpoints.get(root);
}

/**
 * Restores [contenteditable] on the next real user interaction (mousedown or non-modifier keydown).
 * Listens on `document` (not rootElement) so clicks outside the picker still trigger the restore.
 * Modifier-only keys and Cmd/Ctrl+C are skipped to allow selection extension and copy.
 */
function deferContentEditableRestore(root: HTMLElement): void {
  // Cancel any pending restore for this root before registering a new one.
  pendingRestoreSignals.get(root)?.abort();

  const restoreSignal = new AbortController();
  pendingRestoreSignals.set(root, restoreSignal);

  function handler(e: Event): void {
    // Skip restore on right-click; the contextmenu handler deals with it below.
    if (e instanceof MouseEvent && e.button === 2) {
      return;
    }

    if (e instanceof KeyboardEvent) {
      if (shouldBypassRestore(e)) {
        return;
      }
      if (tryCollapseArrow(e, root)) {
        return;
      }
    }

    // Firefox discards cross-element selections the moment [contenteditable]
    // is re-added. Snapshot the current selection endpoints before restoring
    // CE, then recreate the range via setBaseAndExtent so that subsequent
    // bubble-phase handlers (Backspace/Delete bulkClear, Cmd+X, type-to-replace)
    // still see the multi-element selection. Same trick used at mouseup time
    // in removeContentEditable.
    const selection = globalThis.getSelection();
    const endpoints = getMultiElementEndpoints(selection);

    // Stash the pre-restore endpoints for bubble-phase consumers (Safari only
    // truly needs them, but stashing unconditionally keeps the code path
    // browser-agnostic). Bubble handlers fall back to the live selection when
    // no stash is present.
    // Only stashes keyboard events: a click collapses the selection before Backspace fires,
    // making any stash set during mousedown stale and causing bulk-clear to clear the wrong fields.
    if (endpoints !== null && e instanceof KeyboardEvent) {
      lastPreRestoreEndpoints.set(root, endpoints);
    }

    restoreContentEditableAttributes(root);
    pendingRestoreSignals.delete(root);
    restoreSignal.abort();

    if (endpoints !== null) {
      safeSetBaseAndExtent(
        selection,
        endpoints,
        'setBaseAndExtent failed when restoring selection after CE re-attach',
      );
    }
  }
  document.addEventListener('mousedown', handler, {
    signal: restoreSignal.signal,
    capture: true,
  });
  document.addEventListener('keydown', handler, {
    signal: restoreSignal.signal,
    capture: true,
  });

  // On contextmenu, restore CE so the browser shows Cut/Copy/Paste items.
  // Recreate the cross-element selection via setBaseAndExtent so Firefox doesn't drop it.
  document.addEventListener(
    'contextmenu',
    () => {
      const liveSelection = globalThis.getSelection();
      const endpoints = getMultiElementEndpoints(liveSelection);

      if (endpoints !== null) {
        lastPreRestoreEndpoints.set(root, endpoints);
      }

      // Stash clipboard text before CE is restored; Chrome/Safari may clip the
      // selection to a single host by copy time.
      let stashedClipboardText: string | null = null;
      if (endpoints !== null) {
        safeSetBaseAndExtent(
          liveSelection,
          endpoints,
          'setBaseAndExtent failed when restoring selection for context menu (pre-CE)',
        );
        stashedClipboardText = buildTimeframeClipboardText();
      }

      // Use root-level CE to keep the selection inside one editing host (required by Firefox).
      if (endpoints !== null) {
        root.setAttribute('contenteditable', 'true');
      } else {
        restoreContentEditableAttributes(root);
      }
      pendingRestoreSignals.delete(root);
      restoreSignal.abort();

      if (endpoints !== null) {
        safeSetBaseAndExtent(
          liveSelection,
          endpoints,
          'setBaseAndExtent failed when restoring selection for context menu',
        );
        // Reapply drag-selected marks — native ::selection won't render without focus.
        markDragSelectedElements(root, buildDocumentOrderedRange(endpoints));
      }

      if (stashedClipboardText !== null) {
        const copyListenerAbort = new AbortController();
        const copyHandler = (copyEvent: Event): void => {
          if (!(copyEvent instanceof ClipboardEvent)) {
            return;
          }
          copyEvent.preventDefault();
          copyEvent.clipboardData?.setData('text/plain', stashedClipboardText);
          copyListenerAbort.abort();
        };
        document.addEventListener('copy', copyHandler, {
          signal: copyListenerAbort.signal,
        });
        document.addEventListener('cut', copyHandler, {
          signal: copyListenerAbort.signal,
        });
        // Abort when the context menu closes without a cut/copy.
        document.addEventListener(
          'mousedown',
          () => copyListenerAbort.abort(),
          { signal: copyListenerAbort.signal, capture: true, once: true },
        );
      }

      // Restore per-spinbutton CE once the context menu closes.
      if (endpoints !== null) {
        const ceCleanupAbort = new AbortController();
        const restorePerSpinButtonCE = (): void => {
          root.removeAttribute('contenteditable');
          restoreContentEditableAttributes(root);
          ceCleanupAbort.abort();
        };
        document.addEventListener('mousedown', restorePerSpinButtonCE, {
          signal: ceCleanupAbort.signal,
          capture: true,
          once: true,
        });
        document.addEventListener('cut', restorePerSpinButtonCE, {
          signal: ceCleanupAbort.signal,
          once: true,
        });
        document.addEventListener('copy', restorePerSpinButtonCE, {
          signal: ceCleanupAbort.signal,
          once: true,
        });
      }
    },
    { signal: restoreSignal.signal, capture: true },
  );
}

/**
 * Strips [contenteditable] to allow cross-element selection, then restores it on the next `eventType`.
 * `initialAnchorSpinButton` is the spinbutton resolved from bounding-box geometry before CE is stripped —
 * used when Safari misreports anchorNode as a container element on far-right clicks.
 * @internal
 */
export function removeContentEditable(
  rootElement: HTMLElement,
  removeEventTarget: Document | HTMLElement,
  eventType: 'mouseup' | 'keyup',
  customRemovalFn?: (event: Event, hasMultiElementSelection: boolean) => void,
  initialAnchorSpinButton?: HTMLElement | null,
): void {
  // Guard against focus-side effects (e.g. spinbutton onFocus selectAll)
  // while cross-element selection is restored.
  rootElement.setAttribute('data-restoring-selection', '');

  const contentEditables =
    rootElement.querySelectorAll<HTMLElement>('[contenteditable]');
  for (const editable of contentEditables) {
    const previousValue = editable.getAttribute('contenteditable') ?? undefined;
    editable.removeAttribute('contenteditable');
    editable.dataset.wascontenteditable = previousValue;

    // Also strip tabindex to prevent auto-focus on mousedown during drag.
    if (editable.hasAttribute('tabindex')) {
      editable.dataset.wastabindex = editable.getAttribute('tabindex') ?? '';
      editable.removeAttribute('tabindex');
    }
  }

  const signal = new AbortController();

  // Safari reports the container element (not a text node) as anchorNode when the click lands in empty
  // space. Correct the drag anchor on mousemove using the pre-resolved spinbutton's last char position.
  // Subsequent moves no-op once anchorNode becomes a text node; Chrome/Firefox already report text nodes.
  if (initialAnchorSpinButton) {
    const anchorPos = findLastCharacterPosition(initialAnchorSpinButton);
    if (anchorPos) {
      rootElement.addEventListener(
        'mousemove',
        () => {
          const sel = globalThis.getSelection();
          if (!sel || !sel.anchorNode) {
            return;
          }
          // Already corrected.
          if (sel.anchorNode.nodeType === Node.TEXT_NODE) {
            return;
          }
          const focusNode = sel.focusNode;
          if (!focusNode) {
            return;
          }
          const focusOffset = sel.focusOffset ?? 0;
          const anchorBeforeFocus =
            compareSelectionPoints(
              anchorPos.node,
              anchorPos.offset,
              focusNode,
              focusOffset,
            ) <= 0;
          sel.removeAllRanges();
          const correctedEndpoints: SelectionEndpoints = anchorBeforeFocus
            ? {
                anchorNode: anchorPos.node,
                anchorOffset: anchorPos.offset,
                focusNode,
                focusOffset,
              }
            : {
                anchorNode: focusNode,
                anchorOffset: focusOffset,
                focusNode: anchorPos.node,
                focusOffset: anchorPos.offset,
              };
          safeSetBaseAndExtent(
            sel,
            correctedEndpoints,
            'selection anchor correction during drag failed',
          );
        },
        { signal: signal.signal, capture: true },
      );
    }
  }

  // Update [data-drag-selected] on mousemove for element-level accent styling (Safari ::selection workaround).
  rootElement.addEventListener(
    'mousemove',
    () => {
      const sel = globalThis.getSelection();
      if (!sel || sel.rangeCount === 0) {
        return;
      }
      const range = sel.getRangeAt(0);
      markDragSelectedElements(rootElement, range);
    },
    { signal: signal.signal, capture: true },
  );

  removeEventTarget.addEventListener(
    eventType,
    (event: Event) => {
      // Snapshot before restoring CE — Firefox discards cross-element selections when CE is re-added.
      const multiElementEndpoints = getMultiElementEndpoints(
        globalThis.getSelection(),
      );

      if (multiElementEndpoints !== null) {
        const {
          anchorNode: snapshotAnchorNode,
          anchorOffset: snapshotAnchorOffset,
          focusNode: snapshotFocusNode,
          focusOffset: snapshotFocusOffset,
        } = multiElementEndpoints;
        // Multi-element path: keep CE stripped so Safari's setBaseAndExtent isn't clipped to the active
        // editing host. CE is restored lazily on the next real user interaction.

        // Safari may report a container element as anchorNode on empty-space clicks; use initialAnchorSpinButton instead.
        const anchorIsElementNode =
          snapshotAnchorNode.nodeType !== Node.TEXT_NODE;

        // Resolve anchor spinbutton: element-node → use precomputed; text in SB → resolve; text outside → null.
        const anchorNodeWasInContent = anchorIsElementNode
          ? initialAnchorSpinButton !== null
          : isEndpointInsideSpinButtonContent(
              snapshotAnchorNode,
              snapshotAnchorOffset,
            );

        let anchorSpinButton: HTMLElement | null;
        if (anchorIsElementNode) {
          anchorSpinButton = initialAnchorSpinButton ?? null;
        } else if (anchorNodeWasInContent) {
          anchorSpinButton = resolveSpinButton(
            snapshotAnchorNode,
            rootElement,
            snapshotAnchorOffset,
          );
        } else {
          anchorSpinButton = null;
        }

        // When anchor is an element node, derive a text-node position for direction detection.
        let anchorNodeForComparison: Node = snapshotAnchorNode;
        let anchorOffsetForComparison: number = snapshotAnchorOffset;
        if (anchorIsElementNode && anchorSpinButton) {
          const lastPos = findLastCharacterPosition(anchorSpinButton);
          if (lastPos) {
            anchorNodeForComparison = lastPos.node;
            anchorOffsetForComparison = lastPos.offset;
          }
        }

        const focusNodeWasInContent = isEndpointInsideSpinButtonContent(
          snapshotFocusNode,
          snapshotFocusOffset,
        );
        const focusSpinButton =
          focusNodeWasInContent || snapshotFocusNode.nodeType !== Node.TEXT_NODE
            ? resolveSpinButton(
                snapshotFocusNode,
                rootElement,
                snapshotFocusOffset,
              )
            : null;

        const anchorBeforeFocus =
          compareSelectionPoints(
            anchorNodeForComparison,
            anchorOffsetForComparison,
            snapshotFocusNode,
            snapshotFocusOffset,
          ) <= 0;

        let anchorPos: { node: Node; offset: number } | null;
        if (anchorSpinButton?.getAttribute('role') === 'spinbutton') {
          anchorPos = anchorBeforeFocus
            ? findFirstCharacterPosition(anchorSpinButton)
            : findLastCharacterPosition(anchorSpinButton);
        } else {
          anchorPos = {
            node: anchorNodeForComparison,
            offset: anchorOffsetForComparison,
          };
        }
        let focusPos: { node: Node; offset: number } | null;
        if (focusSpinButton?.getAttribute('role') === 'spinbutton') {
          focusPos = anchorBeforeFocus
            ? findLastCharacterPosition(focusSpinButton)
            : findFirstCharacterPosition(focusSpinButton);
        } else {
          focusPos = { node: snapshotFocusNode, offset: snapshotFocusOffset };
        }

        if (anchorPos && focusPos) {
          const dragEndpoints: SelectionEndpoints = {
            anchorNode: anchorPos.node,
            anchorOffset: anchorPos.offset,
            focusNode: focusPos.node,
            focusOffset: focusPos.offset,
          };
          safeSetBaseAndExtent(
            globalThis.getSelection(),
            dragEndpoints,
            'setBaseAndExtent failed when restoring drag selection',
          );
          // Mark intersected elements for accent styling. Exclude textboxes — they need char-level selection.
          markDragSelectedElements(
            rootElement,
            buildDocumentOrderedRange(dragEndpoints),
          );
        }

        customRemovalFn?.(event, true);
        rootElement.removeAttribute('data-restoring-selection');

        // Restore CE on the next user interaction (mousedown / non-modifier keydown).
        deferContentEditableRestore(rootElement);
      } else {
        // Single-element path: restore CE immediately — both endpoints share the same editing host.
        restoreContentEditableAttributes(rootElement);

        customRemovalFn?.(event, false);
        rootElement.removeAttribute('data-restoring-selection');
      }

      signal.abort();
    },
    { signal: signal.signal },
  );
}

/**
 * For Shift+Arrow on absolute spinbuttons, snaps the focus endpoint to a spinbutton boundary
 * rather than stepping character by character (intra-spinbutton steps are visually silent).
 * Growing forward/backward includes the adjacent spinbutton fully; shrinking excludes the current one.
 * Returns null when focus is outside a spinbutton, falling through to char-by-char logic.
 */
function findSpinButtonSnappedPosition(
  root: HTMLElement,
  { anchorNode, anchorOffset, focusNode, focusOffset }: SelectionEndpoints,
  direction: 'forward' | 'backward',
): { node: Node; offset: number } | null {
  let spinButton: HTMLElement | null = null;

  if (focusNode.nodeType === Node.TEXT_NODE) {
    spinButton =
      (focusNode as Text).parentElement?.closest<HTMLElement>(
        '[role="spinbutton"]',
      ) ?? null;
  } else if (
    focusNode instanceof HTMLElement &&
    focusNode.getAttribute('role') === 'spinbutton'
  ) {
    // selectNodeContents(el) sets focusNode to the element itself.
    spinButton = focusNode;
  }

  if (!spinButton) {
    return null;
  }

  const allSBs = Array.from(
    root.querySelectorAll<HTMLElement>('[role="spinbutton"]'),
  );
  const idx = allSBs.indexOf(spinButton);

  // Determine whether the selection is growing or shrinking.
  const focusIsBeforeAnchor =
    compareSelectionPoints(focusNode, focusOffset, anchorNode, anchorOffset) <
    0;
  const isGrowing =
    direction === 'forward' ? !focusIsBeforeAnchor : focusIsBeforeAnchor;

  if (direction === 'forward') {
    const nextSB = allSBs[idx + 1] ?? null;
    if (!nextSB) {
      return null;
    }
    // Growing: snap to far end of next SB. Shrinking: snap to near end (exclude current).
    // Empty placeholder spinbuttons have no text-node children, so findLastCharacterPosition
    // returns null for them. Fall back to the element itself at offset 0, which is a valid
    // and stable selection position for setBaseAndExtent.
    if (isGrowing) {
      return findLastCharacterPosition(nextSB) ?? { node: nextSB, offset: 0 };
    }
    return findFirstCharacterPosition(nextSB) ?? { node: nextSB, offset: 0 };
  }

  const prevSB = allSBs[idx - 1] ?? null;
  if (!prevSB) {
    return null;
  }
  if (isGrowing) {
    return findFirstCharacterPosition(prevSB) ?? { node: prevSB, offset: 0 };
  }
  return findLastCharacterPosition(prevSB) ?? { node: prevSB, offset: 0 };
}

export function extendSelection(
  key: 'ArrowLeft' | 'ArrowRight' | 'Home' | 'End',
): void {
  const selection = globalThis.getSelection();
  if (selection === null) {
    return;
  }

  // Safari does not always register a selection anchor when selectAll() calls
  // addRange() with a collapsed range on an empty (placeholder) spinbutton
  // element that has no text-node children. Recover by treating the focused
  // element itself as both anchor and focus so Shift+Arrow can still start
  // a selection from that spinbutton.
  const activeElement = globalThis.document?.activeElement;
  const hasMissingEndpoints =
    selection.anchorNode === null || selection.focusNode === null;
  if (
    hasMissingEndpoints &&
    isEmptyPlaceholderSpinButton(activeElement as Node)
  ) {
    selection.setBaseAndExtent(
      activeElement as Node,
      0,
      activeElement as Node,
      0,
    );
  }

  if (
    selection.anchorNode === null ||
    selection.focusNode === null ||
    selection.rangeCount < 1
  ) {
    return;
  }

  const anchorNode = selection.anchorNode;
  const anchorOffset = selection.anchorOffset;
  const focusNode = selection.focusNode;
  const focusOffset = selection.focusOffset;

  let nextPos: { node: Node; offset: number } | null = null;

  const root =
    focusNode.nodeType === Node.TEXT_NODE
      ? (focusNode as Text).parentElement?.closest<HTMLDivElement>(
          '.strato-inputgroup',
        )
      : focusNode instanceof HTMLElement
      ? focusNode.closest<HTMLDivElement>('.strato-inputgroup')
      : null;
  if (root === null || root === undefined) {
    return;
  }

  const selEndpoints: SelectionEndpoints = {
    anchorNode,
    anchorOffset,
    focusNode,
    focusOffset,
  };

  if (key === 'ArrowLeft') {
    nextPos =
      findSpinButtonSnappedPosition(root, selEndpoints, 'backward') ??
      findPreviousCharacterPosition(root, focusNode, focusOffset);
  } else if (key === 'ArrowRight') {
    nextPos =
      findSpinButtonSnappedPosition(root, selEndpoints, 'forward') ??
      findNextCharacterPosition(root, focusNode, focusOffset);
  } else if (key === 'Home') {
    nextPos = findFirstCharacterPosition(root);
  } else if (key === 'End') {
    nextPos = findLastCharacterPosition(root);
  }

  // Skip the "→" separator (outside any CE) so Shift+Arrow crosses it in one keypress.
  let crossedSeparator = false;
  while (nextPos && getClosestContentEditable(nextPos.node) === null) {
    crossedSeparator = true;
    const skipped =
      key === 'ArrowLeft' || key === 'Home'
        ? findPreviousCharacterPosition(root, nextPos.node, nextPos.offset)
        : findNextCharacterPosition(root, nextPos.node, nextPos.offset);
    if (!skipped) {
      break;
    }
    nextPos = skipped;
  }

  // After crossing a separator, snap to the boundary of the landing text node rather
  // than stepping one character into it (which is what findNext/PreviousCharacterPosition does).
  if (crossedSeparator && nextPos?.node.nodeType === Node.TEXT_NODE) {
    if (key === 'ArrowRight') {
      nextPos = { node: nextPos.node, offset: 0 };
    } else if (key === 'ArrowLeft') {
      nextPos = {
        node: nextPos.node,
        offset: nextPos.node.nodeValue?.length ?? 0,
      };
    }
  }

  if (!nextPos) {
    return;
  }

  // SPIN_BUTTON_SELECTOR covers both live [contenteditable] and temporarily stripped [data-wascontenteditable].
  const spinButtonFromNode = (node: Node): HTMLElement | null => {
    const el =
      node.nodeType === Node.TEXT_NODE
        ? (node as Text).parentElement
        : node instanceof HTMLElement
        ? node
        : null;
    const sb = el?.closest<HTMLElement>(SPIN_BUTTON_SELECTOR) ?? null;
    return sb?.getAttribute('role') === 'spinbutton' ? sb : null;
  };

  const anchorSB = spinButtonFromNode(anchorNode);

  // Strip CE when the new focus crosses into a different editing host — Safari clips setBaseAndExtent to the active one.
  const anchorCE = getClosestContentEditable(anchorNode);
  const nextCE = getClosestContentEditable(nextPos.node);
  const crossesBoundary = anchorCE !== null && anchorCE !== nextCE;

  if (crossesBoundary) {
    // selectNodeContents sets anchorNode to the element itself; resolve to a text-node position instead.
    // For empty placeholder spinbuttons (no children), skip re-resolution — the element at offset 0 is
    // already the correct and stable anchor. Re-resolving would return the element itself anyway and
    // would cause the selection to appear stuck across successive Shift+Arrow presses.
    let resolvedAnchorNode = anchorNode;
    let resolvedAnchorOffset = anchorOffset;
    const anchorSpinButtonHasContent =
      anchorSB !== null && anchorSB.childNodes.length > 0;
    if (anchorNode.nodeType !== Node.TEXT_NODE && anchorSpinButtonHasContent) {
      // Use the spinbutton boundary that covers the full anchor SB in the extension direction.
      const anchorBeforeFocus =
        compareSelectionPoints(
          anchorNode,
          anchorOffset,
          nextPos.node,
          nextPos.offset,
        ) <= 0;
      const resolved = anchorBeforeFocus
        ? findFirstCharacterPosition(anchorSB)
        : findLastCharacterPosition(anchorSB);
      if (resolved) {
        resolvedAnchorNode = resolved.node;
        resolvedAnchorOffset = resolved.offset;
      }
    }

    // Strip CE so Safari's addRange is not clipped to the editing host.
    root.setAttribute('data-restoring-selection', '');
    for (const editable of root.querySelectorAll<HTMLElement>(
      '[contenteditable]',
    )) {
      const prev = editable.getAttribute('contenteditable') ?? '';
      editable.removeAttribute('contenteditable');
      editable.dataset.wascontenteditable = prev;
    }

    const nextEndpoints: SelectionEndpoints = {
      anchorNode: resolvedAnchorNode,
      anchorOffset: resolvedAnchorOffset,
      focusNode: nextPos.node,
      focusOffset: nextPos.offset,
    };
    // setBaseAndExtent preserves anchor/focus direction (addRange would flip them, reversing all subsequent steps).
    safeSetBaseAndExtent(
      selection,
      nextEndpoints,
      'setBaseAndExtent failed when extending cross-boundary selection',
    );
    markDragSelectedElements(root, buildDocumentOrderedRange(nextEndpoints));

    root.removeAttribute('data-restoring-selection');
    deferContentEditableRestore(root);
  } else {
    // Same editing host: setBaseAndExtent is safe. Guard prevents Chrome's implicit focus from triggering selectAll().
    root.setAttribute('data-restoring-selection', '');
    const nextEndpoints: SelectionEndpoints = {
      anchorNode,
      anchorOffset,
      focusNode: nextPos.node,
      focusOffset: nextPos.offset,
    };
    safeSetBaseAndExtent(
      selection,
      nextEndpoints,
      'setBaseAndExtent failed when extending selection',
    );
    root.removeAttribute('data-restoring-selection');
    // Refresh [data-drag-selected] to clear stale marks when the selection shrinks.
    markDragSelectedElements(root, buildDocumentOrderedRange(nextEndpoints));
  }
  // [data-shift-selection] suppresses :focus background while a Shift+Arrow selection is active; cleared in onFocus.
  root.setAttribute('data-shift-selection', '');
}

/**
 * Helper function that allows to expand the selection to the entire range.
 * @internal
 */
export function extendSelectionToAll(): void {
  const selection = globalThis.getSelection();
  if (
    selection === null ||
    selection.focusNode === null ||
    selection.rangeCount < 1
  ) {
    return;
  }

  const root =
    selection.focusNode.parentElement?.closest<HTMLDivElement>(
      '.strato-inputgroup',
    );
  if (root === null || root === undefined) {
    return;
  }

  const firstPosition = findFirstCharacterPosition(root);
  const lastPosition = findLastCharacterPosition(root);

  if (!firstPosition || !lastPosition) {
    return;
  }

  // Strip CE so Safari cannot clip addRange to the focused editing host (same pattern as removeContentEditable).
  root.setAttribute('data-restoring-selection', '');
  for (const editable of root.querySelectorAll<HTMLElement>(
    '[contenteditable]',
  )) {
    const prev = editable.getAttribute('contenteditable') ?? '';
    editable.removeAttribute('contenteditable');
    editable.dataset.wascontenteditable = prev;

    // strip tabindex to prevent auto-focus
    if (editable.hasAttribute('tabindex')) {
      editable.dataset.wastabindex = editable.getAttribute('tabindex') ?? '';
      editable.removeAttribute('tabindex');
    }
  }

  // Focus first: Firefox/Safari collapse the selection when a role="textbox" blurs,
  // so setBaseAndExtent must run after focus settles. data-restoring-selection
  // suppresses any selectAll triggered by the focus.
  root.focus();

  const allEndpoints: SelectionEndpoints = {
    anchorNode: firstPosition.node,
    anchorOffset: firstPosition.offset,
    focusNode: lastPosition.node,
    focusOffset: lastPosition.offset,
  };
  safeSetBaseAndExtent(
    selection,
    allEndpoints,
    'setBaseAndExtent failed when extending selection to all',
  );
  // Mark all elements in the full range for element-level accent styling.
  markDragSelectedElements(root, buildDocumentOrderedRange(allEndpoints), {
    includeTextboxes: true,
  });

  root.removeAttribute('data-restoring-selection');
  deferContentEditableRestore(root);
}

function getBoundaryNodes(
  direction: 'left' | 'right',
  { anchorNode, anchorOffset, focusNode, focusOffset }: SelectionEndpoints,
) {
  const anchorBeforeFocus =
    compareSelectionPoints(anchorNode, anchorOffset, focusNode, focusOffset) <=
    0;
  if (direction === 'left' && anchorBeforeFocus) {
    return { boundaryNode: anchorNode, boundaryOffset: anchorOffset };
  }
  return { boundaryNode: focusNode, boundaryOffset: focusOffset };
}

/**
 * Collapses a multi-element selection (anchorNode !== focusNode) to one of its
 * boundaries and focuses the spinbutton or expression input at that boundary.
 *
 * Mirrors the natural Chrome/Safari behaviour of plain ArrowLeft/ArrowRight on a
 * non-collapsed selection: ArrowLeft → collapse to start, ArrowRight → collapse
 * to end. Firefox does not collapse — it shrinks the selection one step at a
 * time — so without this helper a Shift+Arrow extension followed by plain
 * arrows would require N presses before the cursor finally moves on Firefox.
 *
 * Returns true when a collapse was performed (callers should preventDefault),
 * false when the selection does not need handling here.
 * @internal
 */
export function collapseMultiElementSelection(
  rootElement: HTMLElement,
  direction: 'left' | 'right',
): boolean {
  const selection = globalThis.getSelection();
  if (selection === null || selection.rangeCount < 1 || selection.isCollapsed) {
    return false;
  }
  const anchorNode = selection.anchorNode;
  const focusNode = selection.focusNode;
  if (anchorNode === null || focusNode === null || anchorNode === focusNode) {
    return false;
  }
  if (!rootElement.contains(anchorNode) && !rootElement.contains(focusNode)) {
    return false;
  }

  const anchorOffset = selection.anchorOffset;
  const focusOffset = selection.focusOffset;

  const { boundaryNode, boundaryOffset } = getBoundaryNodes(direction, {
    anchorNode,
    anchorOffset,
    focusNode,
    focusOffset,
  });

  // Cancel any deferred CE-restore (set up by Shift+Arrow / drag-select) and
  // restore [contenteditable] synchronously so .focus() below lands on a live
  // editable element. Also clears stale [data-drag-selected] marks.
  pendingRestoreSignals.get(rootElement)?.abort();
  pendingRestoreSignals.delete(rootElement);
  restoreContentEditableAttributes(rootElement);
  rootElement.removeAttribute('data-shift-selection');

  const target = resolveSpinButton(boundaryNode, rootElement, boundaryOffset);

  selection.removeAllRanges();

  if (target === null) {
    return false;
  }
  target.focus();
  return true;
}

// Matches spinbuttons in both normal ([contenteditable]) and temporarily stripped ([data-wascontenteditable]) states.
const SPIN_BUTTON_SELECTOR = '[contenteditable], [data-wascontenteditable]';

function getClosestContentEditable(node: Node): HTMLElement | null {
  return node.nodeType === Node.TEXT_NODE
    ? (node as Text).parentElement?.closest<HTMLElement>(
        SPIN_BUTTON_SELECTOR,
      ) ?? null
    : (node as Element).closest?.(SPIN_BUTTON_SELECTOR) ?? null;
}

function isExpressionNode(node: Node | null): boolean {
  return (
    node !== null &&
    getClosestContentEditable(node)?.getAttribute('role') === 'textbox'
  );
}

/**
 * Resolve a selection endpoint node to its containing spinbutton or, when the
 * endpoint lies on a separator between spinbuttons, to the nearest spinbutton.
 * Uses `[data-wascontenteditable]` to identify spinbuttons while contenteditable
 * is temporarily stripped during the drag, and falls back to the restored
 * `[contenteditable]` attribute afterwards.
 *
 * @param offset - When `node` is an element node (not a text node), this is
 *   the child index from the Selection API. The child just before this position
 *   is used as the starting point for the reverse spinbutton search, so that a
 *   click on empty space after the last spinbutton resolves to that spinbutton
 *   rather than falling back to the first one.
 */
function resolveSpinButton(
  node: Node,
  root: HTMLElement,
  offset?: number,
): HTMLElement | null {
  // Happy path: the node is inside a spinbutton.
  const direct = getClosestContentEditable(node);
  if (direct !== null) {
    return direct;
  }

  // Element node: offset is a child index. Walk back from offset-1 to find the nearest spinbutton.
  if (node.nodeType !== Node.TEXT_NODE && offset !== undefined && offset > 0) {
    const childBefore = node.childNodes[offset - 1];
    if (childBefore) {
      const nearest = getNearestPrecedingSpinButton(childBefore, root);
      if (nearest) {
        return nearest;
      }
    }
  }

  // Separator or offset-0 element: fall back to nearest preceding spinbutton.
  return getNearestPrecedingSpinButton(node, root);
}

/**
 * When a drag ends on a separator (e.g. "/" or "."), the selection endpoint is
 * outside any spinbutton. Walk the contenteditable spinbuttons in document order
 * and return the one that immediately precedes the given node in document order.
 * Falls back to the first spinbutton when the node comes before all of them.
 */
function getNearestPrecedingSpinButton(
  node: Node,
  root: HTMLElement,
): HTMLElement | null {
  const spinButtons = Array.from(
    root.querySelectorAll<HTMLElement>(SPIN_BUTTON_SELECTOR),
  );
  if (spinButtons.length === 0) {
    return null;
  }

  let nearest: HTMLElement | null = null;
  for (const spinButton of spinButtons) {
    // DOCUMENT_POSITION_FOLLOWING is set for both "after" and "contained by" — both mean the node comes after this spinButton.
    const position = spinButton.compareDocumentPosition(node);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) {
      nearest = spinButton;
    }
  }

  // If node precedes all spinbuttons, return the first one
  return nearest ?? spinButtons[0] ?? null;
}

/**
 * Determines whether a captured endpoint is truly inside spinbutton content.
 * Separator nodes and caret positions at spinbutton edges must preserve their
 * exact boundary instead of snapping to the whole spinbutton.
 */
function isEndpointInsideSpinButtonContent(
  node: Node,
  offset: number,
): boolean {
  const closest = getClosestContentEditable(node);
  if (!closest || closest.getAttribute('role') !== 'spinbutton') {
    return false;
  }

  const firstChild = closest.childNodes[0] ?? null;
  const leadingTextNode =
    firstChild?.nodeType === Node.TEXT_NODE
      ? firstChild
      : firstChild?.firstChild?.nodeType === Node.TEXT_NODE
      ? firstChild.firstChild
      : null;
  const isLeadingContentNode =
    node === firstChild ||
    node === leadingTextNode ||
    (node instanceof Element &&
      leadingTextNode !== null &&
      node.contains(leadingTextNode)) ||
    (node.nodeType === Node.TEXT_NODE &&
      firstChild instanceof Element &&
      firstChild.contains(node));
  const nodeLength =
    node.nodeType === Node.TEXT_NODE
      ? node.nodeValue?.length ?? 0
      : node.textContent?.length ?? 0;
  const isBoundaryOffset = offset === 0 || offset === nodeLength;

  return !(isLeadingContentNode && isBoundaryOffset);
}

/**
 * One entry in a spin-button undo snapshot.
 * Uses a stable inputRef + Map key pair instead of a direct handler reference
 * so that the undo step always resolves the *current* (post-re-render) handler.
 * @internal
 */
export type SpinButtonSnapshotEntry = {
  inputRef: RefObject<DateTimeInputRef>;
  key: Intl.DateTimeFormatPartTypes;
  previousText: string | null;
};

type Entry = {
  inputRef: RefObject<DateTimeInputRef>;
  key: Intl.DateTimeFormatPartTypes;
  element: HTMLSpanElement;
};

/**
 * Inspects the spin buttons of the supplied inputs and returns a snapshot for
 * every spin button whose content overlaps the selected range.
 *
 * The snapshot captures the current `textContent` of each covered element so
 * it can later be passed to.
 * @internal
 */
export function buildSpinButtonSnapshot(
  anchorNode: Node | null,
  anchorOffset: number,
  focusNode: Node | null,
  focusOffset: number,
  inputRefs: RefObject<DateTimeInputRef>[],
): SpinButtonSnapshotEntry[] {
  if (!anchorNode || !focusNode) {
    return [];
  }

  const anchorBeforeFocus =
    compareSelectionPoints(anchorNode, anchorOffset, focusNode, focusOffset) <=
    0;
  const selectionStartNode = anchorBeforeFocus ? anchorNode : focusNode;
  const selectionStartOffset = anchorBeforeFocus ? anchorOffset : focusOffset;
  const selectionEndNode = anchorBeforeFocus ? focusNode : anchorNode;
  const selectionEndOffset = anchorBeforeFocus ? focusOffset : anchorOffset;

  const entries: Entry[] = [];
  for (const inputRef of inputRefs) {
    if (!inputRef.current) {
      continue;
    }

    for (const [
      key,
      handler,
    ] of inputRef.current.spinButtonHandlers.entries()) {
      const el = handler?.element.current;
      if (el) {
        entries.push({ inputRef, key, element: el });
      }
    }
  }

  return entries
    .filter(({ element }) =>
      selectionOverlapsSpinButton(
        selectionStartNode,
        selectionStartOffset,
        selectionEndNode,
        selectionEndOffset,
        element,
      ),
    )
    .map(({ inputRef, key, element }) => ({
      inputRef,
      key,
      previousText: element.textContent ?? null,
    }));
}

/**
 * Restores spin-button values from a previously captured snapshot.
 * For each entry the current handler is resolved via the stable `inputRef` +
 * Map key, so stale handler references from before a React re-render are never
 * used.
 * @internal
 */
export function restoreSpinButtonSnapshot(
  snapshot: SpinButtonSnapshotEntry[],
): void {
  for (const { inputRef, key, previousText } of snapshot) {
    const handler = inputRef.current?.spinButtonHandlers.get(key);
    if (!handler) {
      continue;
    }

    if (handler.type === 'number') {
      // applyText updates DOM, internal state, and fires onChange atomically.
      handler.applyText(previousText ?? '', true);
    } else if (previousText !== null && previousText.length > 0) {
      // Write to DOM immediately to avoid a visual flash before re-render.
      if (handler.element.current) {
        handler.element.current.textContent = previousText;
      }
      // searchValue updates internal state and fires onChange.
      handler.searchValue(previousText.charAt(0), true);
    } else {
      handler.clearValue(true);
    }
  }
}

/**
 * After a bulk-clear, determines the expression value to apply for each affected input:
 * - **0 survivors** (all spin buttons cleared): resets the input to an empty expression (`""`).
 * - **1 survivor** (exactly one spin button was outside the selection): carries its value
 *   into expression mode via `setExpressionValue` rather than leaving a partial date.
 * - **2+ survivors** (partial deletion): leaves the remaining spin buttons as-is.
 *
 * **Must be called BEFORE the clear loop** so that textContent is read while
 * the DOM is still intact (TextSpinButton clears its DOM only on next render).
 * Returns a Map of inputRef → expression text for every input that qualifies.
 * After clearing, iterate the map and call `inputRef.current.setExpressionValue(text)`.
 * @internal
 */
export function findExpressionFallbacks(
  snapshot: SpinButtonSnapshotEntry[],
): Map<RefObject<DateTimeInputRef>, string> {
  const result = new Map<RefObject<DateTimeInputRef>, string>();

  for (const inputRef of new Set(snapshot.map((e) => e.inputRef))) {
    if (!inputRef.current) {
      continue;
    }

    const clearedKeys = new Set(
      snapshot.filter((e) => e.inputRef === inputRef).map((e) => e.key),
    );

    // Collect the spin buttons that were NOT part of the deletion.
    const survivors = [...inputRef.current.spinButtonHandlers.entries()].filter(
      ([key]) => !clearedKeys.has(key),
    );

    if (survivors.length === 0) {
      // Everything was cleared — reset the input to an empty expression.
      result.set(inputRef, '');
    } else if (survivors.length === 1) {
      // Exactly one spin button survived — carry its value into expression mode.
      const [, handler] = survivors[0];
      const text = handler?.element.current?.textContent ?? '';
      if (text.length > 0) {
        result.set(inputRef, text);
      }
    }
    // 2+ survivors: partial deletion within the input, leave the spin buttons as-is.
  }

  return result;
}

/** Returns the spin-button handler for the first entry in a snapshot, or `undefined`.
 * Returns `undefined` when that entry is the first or second spin button in its
 * group — in those cases callers should fall back to expression-input focus.
 * @internal
 */
export function getFirstSnapshotHandler(snapshot: SpinButtonSnapshotEntry[]) {
  const first = snapshot[0];
  if (!first) {
    return undefined;
  }
  const spinButtonHandlers = first.inputRef.current?.spinButtonHandlers;
  if (!spinButtonHandlers) {
    return undefined;
  }

  // Count only entries belonging to the first inputRef so that a snapshot
  // spanning two inputs does not inflate the count and return a wrong result.
  const clearedInFirstInput = snapshot.filter(
    (e) => e.inputRef === first.inputRef,
  ).length;
  // It switches to relative input either when everything is cleared or when only the first spinbutton remains.
  const switchedToRelative = clearedInFirstInput >= spinButtonHandlers.size - 1;

  if (switchedToRelative) {
    return undefined;
  }

  return spinButtonHandlers.get(first.key);
}

/** Returns the spin-button handler for the last entry in a snapshot, or `undefined`.
 * Returns `undefined` when the last input's cleared count means it switched to
 * relative mode — callers should fall back to expression-input focus in that case.
 * @internal
 */
export function getLastSnapshotHandler(snapshot: SpinButtonSnapshotEntry[]) {
  const last = snapshot[snapshot.length - 1];
  if (!last) {
    return undefined;
  }
  const spinButtonHandlers = last.inputRef.current?.spinButtonHandlers;
  if (!spinButtonHandlers) {
    return undefined;
  }

  // Count only entries belonging to the last inputRef so that a snapshot
  // spanning two inputs does not inflate the count and return a wrong result.
  const clearedInLastInput = snapshot.filter(
    (e) => e.inputRef === last.inputRef,
  ).length;
  // It switches to relative input either when everything is cleared or when only the first spinbutton remains.
  const switchedToRelative = clearedInLastInput >= spinButtonHandlers.size - 1;

  if (switchedToRelative) {
    return undefined;
  }

  return spinButtonHandlers.get(last.key);
}

/**
 * Returns the first ref in `inputRefs` order that has an expression input to
 * focus after a bulk clear, considering both directly cleared expression inputs
 * and inputs that transitioned to expression mode via expressionFallbacks.
 * Using `inputRefs` order ensures focus lands on the leftmost affected input
 * even when the selection's anchor is in an already-empty expression input.
 * @internal
 */
export function findFirstExpressionFocusTarget(
  inputRefs: RefObject<DateTimeInputRef>[],
  affectedExpressionRefs: RefObject<DateTimeInputRef>[],
  expressionFallbacks: Map<RefObject<DateTimeInputRef>, string>,
): RefObject<DateTimeInputRef> | undefined {
  const allAffected = new Set<RefObject<DateTimeInputRef>>([
    ...affectedExpressionRefs,
    ...expressionFallbacks.keys(),
  ]);
  return inputRefs.find((ref) => allAffected.has(ref));
}

/**
 * Returns the expression input element when the selection is non-collapsed but
 * covers only part of the text within a single expression input. In that case
 * the caller should handle the deletion explicitly rather than relying on the
 * bulk-clear path or native browser behaviour.
 * Returns null when the selection is not a partial single-expression selection.
 * @internal
 */
export function getPartialExpressionElement(
  selection: Selection,
  inputRefs: RefObject<DateTimeInputRef>[],
): HTMLSpanElement | null {
  for (const ref of inputRefs) {
    const expressionElement = ref.current?.expressionInputRef;
    if (
      expressionElement &&
      expressionElement.contains(selection.anchorNode) &&
      expressionElement.contains(selection.focusNode) &&
      selection.toString().length < (expressionElement.textContent?.length ?? 0)
    ) {
      return expressionElement;
    }
  }
  return null;
}

/**
 * Returns expression boundaries touched by the current selection.
 * - Two different expressions: `[firstExpressionElement, lastExpressionElement]` in document order.
 * - Mixed boundary (one expression + one non-expression boundary): `[expressionElement, expressionElement]`.
 * - Otherwise: `null`.
 * @internal
 */
export function getCrossExpressionElements(
  selection: Selection,
  inputRefs: RefObject<DateTimeInputRef>[],
): [HTMLSpanElement, HTMLSpanElement] | null {
  if (selection.rangeCount < 1) {
    return null;
  }

  const range = selection.getRangeAt(0);
  const startEl = inputRefs.find((ref) =>
    ref.current?.expressionInputRef?.contains(range.startContainer),
  )?.current?.expressionInputRef;
  const endEl = inputRefs.find((ref) =>
    ref.current?.expressionInputRef?.contains(range.endContainer),
  )?.current?.expressionInputRef;

  if (!startEl && !endEl) {
    return null;
  }

  if (startEl && !endEl) {
    return [startEl, startEl];
  }
  if (!startEl && endEl) {
    return [endEl, endEl];
  }

  if (!startEl || !endEl) {
    return null;
  }

  // A selection fully inside one expression is handled by the existing
  // single-expression paths (partial deletion or full bulk clear).
  if (startEl === endEl) {
    return null;
  }

  // Return in document order.
  const startFirst = !!(
    startEl.compareDocumentPosition(endEl) & Node.DOCUMENT_POSITION_FOLLOWING
  );
  return startFirst ? [startEl, endEl] : [endEl, startEl];
}

/**
 * Focuses an expression element and places the cursor at the given range,
 * or at the end of the element when no range is provided.
 * The data-restoring-selection guard tells the focus handler to skip selectAll().
 * @internal
 */
export function focusExpression(
  expressionElement: HTMLSpanElement,
  range?: Range,
): void {
  const root = expressionElement.closest<HTMLElement>('.strato-inputgroup');
  root?.setAttribute('data-restoring-selection', '');
  expressionElement.focus();
  const sel = globalThis.getSelection();
  if (sel) {
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(expressionElement);
      range.collapse(false);
    }
    sel.setBaseAndExtent(
      range.startContainer,
      range.startOffset,
      range.endContainer,
      range.endOffset,
    );
  }
  root?.removeAttribute('data-restoring-selection');
}

function getOffsetWithinExpression(
  expressionElement: Element,
  container: Node,
  offset: number,
): number {
  const range = document.createRange();
  range.selectNodeContents(expressionElement);
  range.setEnd(container, offset);
  return range.toString().length;
}

/**
 * Returns the character offset of the cursor within `expressionElement`, or
 * `undefined` when no selection is active inside the element.
 * @internal
 */
export function getExpressionCursorOffset(
  expressionElement: Element,
): number | undefined {
  const sel = window.getSelection();
  if (!sel?.rangeCount) {
    return undefined;
  }
  const range = sel.getRangeAt(0);
  if (!expressionElement.contains(range.endContainer)) {
    return undefined;
  }
  return getOffsetWithinExpression(
    expressionElement,
    range.endContainer,
    range.endOffset,
  );
}

/**
 * Places the cursor at `offset` characters into `expressionElement`.
 * Clamps to the element's text length when `offset` exceeds it.
 * @internal
 */
export function restoreExpressionCursorOffset(
  expressionElement: HTMLSpanElement,
  offset: number,
): void {
  const textNode = expressionElement.firstChild;
  const sel = window.getSelection();
  if (textNode instanceof Text && sel) {
    const range = document.createRange();
    range.setStart(textNode, Math.min(offset, textNode.length));
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

/**
 * Deletes the selected range within a single expression input, collapses the
 * cursor to the deletion point, and fires an input event so the expression
 * handler can update React state.
 * @internal
 */
export function deletePartialExpression(
  selection: Selection,
  expressionElement: HTMLSpanElement,
): void {
  const range = selection.getRangeAt(0);
  const text = expressionElement.textContent ?? '';

  const start = expressionElement.contains(range.startContainer)
    ? getOffsetWithinExpression(
        expressionElement,
        range.startContainer,
        range.startOffset,
      )
    : 0;
  const end = expressionElement.contains(range.endContainer)
    ? getOffsetWithinExpression(
        expressionElement,
        range.endContainer,
        range.endOffset,
      )
    : text.length;

  const from = Math.min(start, end);
  const to = Math.max(start, end);
  const nextText = `${text.slice(0, from)}${text.slice(to)}`;
  expressionElement.textContent = nextText;

  const nextRange = document.createRange();
  const node = expressionElement.firstChild;
  if (node?.nodeType === Node.TEXT_NODE) {
    nextRange.setStart(node, Math.min(from, nextText.length));
  } else {
    nextRange.setStart(expressionElement, 0);
  }
  nextRange.collapse(true);

  focusExpression(expressionElement, nextRange);
  expressionElement.dispatchEvent(new InputEvent('input', { bubbles: true }));
}

/**
 * Plans a bulk clear operation by analyzing the current selection
 * and computing everything needed to clear affected spin buttons and expression inputs.
 * Must be called before any DOM writes so that textContent is still intact.
 * @internal
 */
export function planBulkClear(
  selection: Selection,
  inputRefs: RefObject<DateTimeInputRef>[],
  endpointsOverride?: SelectionEndpoints,
): {
  snapshot: SpinButtonSnapshotEntry[];
  affectedExpressionRefs: RefObject<DateTimeInputRef>[];
  expressionFallbacks: Map<RefObject<DateTimeInputRef>, string>;
} {
  // Safari clips a freshly restored cross-CE selection to the active editing
  // host, so callers may pass the pre-restore endpoints captured by
  // `deferContentEditableRestore`. Fall back to the live selection otherwise.
  // Discard the override when either node is no longer connected to the
  // document — this can happen when a paste operation replaces spin-button
  // text nodes between the CE-restore keydown and a subsequent Delete press.
  const validOverride =
    endpointsOverride?.anchorNode.isConnected &&
    endpointsOverride?.focusNode.isConnected
      ? endpointsOverride
      : undefined;
  const anchorNode = validOverride?.anchorNode ?? selection.anchorNode;
  const anchorOffset = validOverride?.anchorOffset ?? selection.anchorOffset;
  const focusNode = validOverride?.focusNode ?? selection.focusNode;
  const focusOffset = validOverride?.focusOffset ?? selection.focusOffset;

  const affectedExpressionRefs = inputRefs.filter((ref) => {
    const expressionInputRef = ref.current?.expressionInputRef;
    return (
      expressionInputRef &&
      (expressionInputRef.contains(anchorNode) ||
        expressionInputRef.contains(focusNode))
    );
  });

  const anchorInExpression = affectedExpressionRefs.some((ref) =>
    ref.current?.expressionInputRef?.contains(anchorNode),
  );
  const focusInExpression = affectedExpressionRefs.some((ref) =>
    ref.current?.expressionInputRef?.contains(focusNode),
  );

  // Skip buildSpinButtonSnapshot only when BOTH boundaries are inside expression inputs.
  const snapshot =
    anchorInExpression && focusInExpression
      ? []
      : buildSpinButtonSnapshot(
          anchorNode,
          anchorOffset,
          focusNode,
          focusOffset,
          inputRefs,
        );

  return {
    snapshot,
    affectedExpressionRefs,
    expressionFallbacks: findExpressionFallbacks(snapshot),
  };
}

/**
 * Deletes the selected portion across two expression inputs using two separate
 * single-element ranges. Using one cross-element range with deleteContents()
 * does not modify text inside contenteditable boundaries; splitting into two
 * intra-element ranges avoids that limitation entirely.
 * Focus is placed at the end of whatever text remains in the first expression.
 * @internal
 */
export function deleteCrossExpressionPartial(
  selection: Selection,
  [firstExpressionElement, lastExpressionElement]: [
    HTMLSpanElement,
    HTMLSpanElement,
  ],
): void {
  if (firstExpressionElement === lastExpressionElement) {
    deletePartialExpression(selection, firstExpressionElement);
    return;
  }

  const { startContainer, startOffset, endContainer, endOffset } =
    selection.getRangeAt(0);

  // Delete from the selection start to the end of firstExpressionElement.
  const fromRange = document.createRange();
  fromRange.setStart(startContainer, startOffset);
  fromRange.setEnd(
    firstExpressionElement,
    firstExpressionElement.childNodes.length,
  );
  fromRange.deleteContents();

  // Delete from the start of lastExpressionElement to the selection end.
  const toRange = document.createRange();
  toRange.setStart(lastExpressionElement, 0);
  toRange.setEnd(endContainer, endOffset);
  toRange.deleteContents();

  // Place cursor at the end of whatever text remains in the first expression.
  selection.removeAllRanges();
  focusExpression(firstExpressionElement);

  firstExpressionElement.dispatchEvent(
    new InputEvent('input', { bubbles: true }),
  );
  lastExpressionElement.dispatchEvent(
    new InputEvent('input', { bubbles: true }),
  );
}

/**
 * Returns the keyup callback passed to `removeContentEditable` after a
 * bulk-clear (cut or delete). Once the key is released and contenteditable
 * is restored, it decides where focus lands:
 * - If `firstElement` is `undefined` (the selection started at a leading spin
 *   button), focus the first affected expression input.
 * - Otherwise keep focus on that spin button.
 * @internal
 */
export function restoreFocusAfterBulkClear(
  firstSpinButtonHandler: SpinButtonHandler | null | undefined,
  focusTarget: RefObject<DateTimeInputRef> | undefined,
) {
  if (firstSpinButtonHandler) {
    firstSpinButtonHandler.element.current?.focus();
    firstSpinButtonHandler.selectAll();
    return;
  }

  const expressionElement = focusTarget?.current?.expressionInputRef;
  if (expressionElement) {
    requestAnimationFrame(() => {
      focusExpression(expressionElement);
    });
  }
}
